/**
 * Investor tax-incentive simulator (Art. 36 LIS).
 *
 * Pure business logic — no React, no framework. Given a taxpayer's taxable base
 * and tax rate, it derives the maximum investment that fully absorbs the 50% of
 * the tax liability the deduction can offset, and the resulting fiscal savings.
 */

/**
 * Art. 36 LIS: the generated deduction that can be applied in the tax period is
 * capped at 50% of the tax liability (cuota íntegra).
 */
const MAX_DEDUCTION_RATIO_OF_LIABILITY = 0.5;

/**
 * Art. 39.7 LIS: the financier's deduction is 1.20x the amount contributed, so
 * the investment that generates a given deduction is that deduction / 1.20.
 */
const DEDUCTION_TO_INVESTMENT_FACTOR = 1.2;

/**
 * Art. 36 LIS caps the investor's contribution at 50% of their taxable base.
 * Used to validate the potential-investment field against the declared base.
 */
export const INVESTOR_CAPACITY_RATIO = 0.5;

export interface CalculatorResults {
  initialTaxLiability: number;
  maxDeduction: number;
  investmentAmount: number;
  taxSavings: number;
  finalTaxLiability: number;
  investmentROI: number;
  taxRate: number;
  taxableBase: number;
}

export function calculateTaxResults(
  taxableBase: string,
  taxRate: string,
): CalculatorResults | null {
  const base = parseFloat(taxableBase);
  const rate = parseFloat(taxRate);

  if (!(base > 0 && rate > 0)) return null;

  // 1. Initial Tax Liability (Cuota Íntegra del IRPF/IS)
  const initialTaxLiability = base * (rate / 100);

  // 2. Max Deduction (Deducción a aplicar en el ejercicio) - 50% of tax liability
  const maxDeduction = initialTaxLiability * MAX_DEDUCTION_RATIO_OF_LIABILITY;

  // 3. Investment Amount (Máximo a invertir) - Deduction divided by 120%
  const investmentAmount = maxDeduction / DEDUCTION_TO_INVESTMENT_FACTOR;

  // 4. Tax Savings (Rentabilidad Fiscal) - Deduction minus Investment
  const taxSavings = maxDeduction - investmentAmount;

  // 5. Final Tax Liability (Cuota final a pagar) - Initial tax minus deduction
  const finalTaxLiability = initialTaxLiability - maxDeduction;

  // 6. Investment ROI %
  const investmentROI = ((maxDeduction - investmentAmount) / investmentAmount) * 100;

  return {
    initialTaxLiability,
    maxDeduction,
    investmentAmount,
    taxSavings,
    finalTaxLiability,
    investmentROI,
    taxRate: rate,
    taxableBase: base,
  };
}

/**
 * Art. 36 LIS: an investor's contribution cannot exceed 50% of their taxable
 * base. Empty or non-numeric inputs are treated as valid (nothing to check yet).
 */
export function isWithinFiscalCapacity(
  amount: string | null | undefined,
  taxableBase: string | null | undefined,
): boolean {
  if (!amount || !taxableBase) return true;
  const base = parseFloat(taxableBase);
  const value = parseFloat(amount);
  if (Number.isNaN(base) || Number.isNaN(value)) return true;
  return value <= base * INVESTOR_CAPACITY_RATIO;
}
